'use client';

import { useRouter } from 'next/navigation';

import { Badge as MokoBadge, Button } from '@mokoboko/components';
import { formatPrice, mergeClassNames } from '@/lib/utils';
import { cardImageOf } from '@/lib/entity-image';
import {
  CARD_SIDE_BOX_CLASS,
  CARD_SIDE_BOX_LINES_CLASS,
  CARD_SIDE_BOX_TITLE_CLASS,
} from '@/lib/prose';
import { getTopupExpiryLabel } from '@/helpers/credits';
import useTopupEligibility from '@/state/topup';
import type { TopupContent } from '@/services/topup';
import { FocalPointImage } from './focal-point-image';

export const TopupListCard = ({ topup }: { topup: TopupContent }) => {
  const router = useRouter();
  const { data: eligibility } = useTopupEligibility();

  if (!eligibility?.eligible) return null;

  const { url: cardImageUrl, focalX, focalY } = cardImageOf(topup);
  const hasCardImage = !!cardImageUrl;

  // Card only renders when eligible, so the price the customer pays is resolved
  // by their attached plan (pack vs subscription).
  const priceLabel = formatPrice(eligibility.price, eligibility.currency);

  const expiryLabel = getTopupExpiryLabel(
    topup.expiry_policy,
    'plan',
    eligibility?.eligible ? eligibility.credits_expire_at : null
  );

  const hasDescription = !!topup.short_description || !!topup.description;

  const goToDetails = () => router.push(`/${topup.slug}`);

  return (
    <div className="flex w-full flex-row gap-4 md:gap-5">
      <div className={mergeClassNames(CARD_SIDE_BOX_CLASS, 'md:w-[140px]')}>
        <div className={CARD_SIDE_BOX_LINES_CLASS}>
          <p className={CARD_SIDE_BOX_TITLE_CLASS}>1 Credit</p>
          <p className="text-sm text-gray-700">{expiryLabel}</p>
        </div>
        <div className="md:hidden">
          <MokoBadge
            label={priceLabel}
            color="gray"
            size="md"
            className="bg-gray-200"
          />
        </div>
      </div>

      <div
        className="group flex w-full cursor-pointer overflow-hidden rounded-xl border border-gray-200 shadow-xs transition-shadow duration-200 max-md:flex-col md:hover:shadow-lg"
        onClick={goToDetails}
      >
        <div
          className={mergeClassNames(
            'flex w-full flex-col gap-5 p-4 md:gap-6 md:p-5',
            !hasDescription ? 'md:gap-5' : ''
          )}
        >
          <div className="flex flex-col gap-2 md:hidden">
            <p className="text-lg font-semibold text-gray-900 group-hover:text-red-500">
              {topup.name}
            </p>
            {topup.short_description && (
              <p className="text-sm text-gray-700">{topup.short_description}</p>
            )}
          </div>

          <div className="hidden w-full flex-row gap-5 md:flex">
            <div className="flex flex-1 flex-col gap-2">
              <p className="text-lg font-semibold text-gray-900 group-hover:text-red-500">
                {topup.name}
              </p>
              <p className="text-sm text-gray-700">{priceLabel}</p>
            </div>
            <div className="shrink-0">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  goToDetails();
                }}
                className="group-hover:bg-red-500"
              >
                Select
              </Button>
            </div>
          </div>

          {topup.short_description && (
            <p className="hidden text-sm text-gray-700 md:block">
              {topup.short_description}
            </p>
          )}

          {hasCardImage && (
            <div className="relative h-[200px] w-full overflow-hidden rounded-xl md:hidden">
              <FocalPointImage
                src={cardImageUrl!}
                focalPointX={focalX}
                focalPointY={focalY}
              />
            </div>
          )}

          <div className="w-full md:hidden">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                goToDetails();
              }}
              fullWidth
              className="group-hover:bg-red-500"
            >
              Select
            </Button>
          </div>
        </div>

        {hasCardImage && (
          <div
            className={mergeClassNames(
              'max-md:pb-1/2 relative hidden h-[200px] w-auto shrink-0 rounded-l-xl max-md:w-full md:block md:h-[100px] md:w-[100px]',
              hasDescription ? 'h-full w-[156px] md:h-full md:w-[156px]' : ''
            )}
          >
            <FocalPointImage
              src={cardImageUrl!}
              focalPointX={focalX}
              focalPointY={focalY}
              className="absolute"
            />
          </div>
        )}
      </div>
    </div>
  );
};
